import React from 'react'
import { v4 as uuidv4 } from 'uuid'
import moment from 'moment'
import { isBefore } from 'date-fns'
import { Divider } from '@mui/material'
import { TimePicker } from '@mui/x-date-pickers'
import { Clear } from '@mui/icons-material'
import { useTheme } from '@mui/material'
import { AddCircle, ArrowDown2, CloseCircle } from 'iconsax-react'

import BaseModal from '../BaseModal2'
import ModalTextField from '../../../atoms/ModalTextField'
import Section from '../IntegrationModal/Section'
import Dropdown from '../../../atoms/Dropdown'
import Tooltip from '../../../atoms/Tooltip'
import Checkbox from '../../../../pages/Settings/Controls/Checkbox'
import AutoInput from '../../AutoInput'
import Button from '../../../atoms/Button2'
import RedButton from '../../../atoms/RedButton'

import { useAppDispatch, useAppSelector } from '../../../../hooks/redux'
import {
  BaseInboxEditType,
  INITIAL_VALUES,
  formatDataFromAPI,
  PROFILE_OPTIONS,
  formatPayload
} from './helpers'
import { DAYS, MIN_TIME, MAX_TIME } from '../IntegrationModal/helpers'
import {
  clearError,
  updateInbox,
  createInbox
} from '../../../../slices/baseInbox'
import { fetchTimezones } from '../../../../slices/timezone'
import { selectTeams, fetchTeams } from '../../../../slices/teamsSlice'
import { useAreYouSure } from '../../../../providers/AreYouSureProvider'
import { AddErrorAlert, AddSuccessAlert } from '../../../../slices/alert'

import useStyles from './styles'

// Used to force clock view
const ALWAYS_FALSE_QUERY = '@media screen and not screen'

interface BaseInboxModalProps {
  onClose: () => void
  data: null | APIBaseInbox
}

const BaseInboxModal: React.FC<BaseInboxModalProps> = ({ onClose, data }) => {
  const dispatch = useAppDispatch()
  const theme = useTheme()

  const {
    timezones,
    current: userTz,
    loaded: timezonesLoaded,
    loading: timezonesLoading
  } = useAppSelector(state => state.timezone)
  const {
    teams,
    loaded: teamsLoaded,
    loading: teamsLoading
  } = useAppSelector(selectTeams)
  const { setAction, setReason, setSubtext, setTitle, setV2 } = useAreYouSure()

  const [values, setValues] = React.useState<null | BaseInboxEditType>(null)
  const [editing, setEditing] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [archiving, setArchiving] = React.useState(false)
  const [changed, setChanged] = React.useState(false)
  const teamOptions = React.useMemo(() => {
    if (!teamsLoaded) {
      return []
    }

    if (!values) {
      return [...teams]
    }

    return teams.filter(team => !values.team_restrict.includes(team.id))
  }, [teamsLoaded, values, teams])

  // Only 3 hours of operation can be added
  const addHoursDisabled = React.useMemo(() => {
    if (!values) {
      return false
    }

    const hours = values.hours
    return Object.keys(hours).length >= 3
  }, [values])

  const classes = useStyles()

  React.useEffect(() => {
    if (!timezonesLoaded && !timezonesLoading) {
      dispatch(fetchTimezones())
    }
  }, [timezonesLoaded, timezonesLoading, dispatch])

  // Loads teams
  React.useEffect(() => {
    if (!teamsLoaded && !teamsLoading) {
      dispatch(fetchTeams())
    }
  }, [teamsLoaded, teamsLoading, dispatch])

  const getUserTimeZone = React.useCallback(() => {
    if (timezones.length > 0) {
      return timezones.find(tz => tz.key === userTz)
    }

    return timezones[0]
  }, [timezones, userTz])

  const getTimeZoneDropdownRenderValue = React.useCallback(
    key => {
      const tz = timezones.find(tz => tz.key === key)?.value ?? null

      if (editing) {
        return tz
      }

      const userTzVal = getUserTimeZone().value
      return tz === userTzVal ? `${userTzVal} (recommended)` : tz
    },
    [timezones, editing, getUserTimeZone]
  )

  const getTimeZoneDropdownRenderChildren = React.useCallback(() => {
    const tz = timezones.find(tz => tz.key === values.timezone)?.value

    if (editing) {
      return tz ?? 'Default'
    }

    const userTzVal = getUserTimeZone().value
    return tz === userTzVal ? `${userTzVal} (recommended)` : tz
  }, [timezones, editing, getUserTimeZone, values])

  // Updates local values if editing
  React.useEffect(() => {
    setEditing(Boolean(data))
    setValues(data ? formatDataFromAPI(data) : INITIAL_VALUES)
  }, [data])

  // Helper method to set error
  const showError = React.useCallback(
    message => {
      dispatch(AddErrorAlert({ err: { message } }))
    },
    [dispatch]
  )

  // Updates stored values
  const handleChange = React.useCallback(
    key => val => {
      setChanged(true)
      if (['string', 'boolean'].includes(typeof val)) {
        setValues(old => ({ ...old, [key]: val }))
      }
    },
    []
  )

  // Updates hour values
  const handleUpdateHours = React.useCallback(
    (type, id) => e => {
      setChanged(true)

      if (type === 'day') {
        const val = e
        setValues(old => {
          const oldDays = old.hours[id].days
          let newDays = [...oldDays]
          if (oldDays.includes(e)) {
            newDays = newDays.filter(d => d !== val)
          } else {
            newDays = [...newDays, val]
          }

          return {
            ...old,
            hours: {
              ...old.hours,
              [id]: {
                ...old.hours[id],
                days: [...new Set(newDays)]
              }
            }
          }
        })
      } else {
        const val = e._d
        setValues(old => ({
          ...old,
          hours: {
            ...old.hours,
            [id]: {
              ...old.hours[id],
              [type]: val
            }
          }
        }))
      }
    },
    []
  )

  // Deletes hour values
  const handleDeleteHours = React.useCallback(
    id => () => {
      setChanged(true)

      setValues(old => {
        const newVals = { ...old }
        delete newVals.hours[id]
        return newVals
      })
    },
    []
  )

  // Adds hours
  const handleAddHours = React.useCallback(
    val => {
      setChanged(true)

      const { newStart, newEnd, newDays: days } = val
      if (!days.length) {
        return showError('Please select at least one day')
      }

      const start = newStart._d ?? newStart
      const end = newEnd._d ?? newEnd

      if (isBefore(end, start)) {
        return showError('End time must come after start time')
      }

      setValues(old => ({
        ...old,
        hours: {
          ...old.hours,
          [uuidv4()]: { start, end, days }
        }
      }))
    },
    [showError]
  )

  const handleTeamSelect = React.useCallback(team => {
    setChanged(true)

    const id = team.id

    setValues(old => {
      let team_restrict = []

      if (old.team_restrict.includes(id)) {
        team_restrict = old.team_restrict.filter(ids => ids !== id)
      } else {
        team_restrict = [...old.team_restrict, id]
      }

      return { ...old, team_restrict }
    })
  }, [])

  // Resets modal values and closes
  const handleClose = React.useCallback(() => {
    setValues(INITIAL_VALUES)
    setEditing(false)
    dispatch(clearError())
    setChanged(false)
    setSaving(false)
    setArchiving(false)
    onClose()
  }, [dispatch, onClose])

  const renderProfile = React.useCallback(key => {
    const val = PROFILE_OPTIONS[key]

    if (!val) {
      return null
    }

    if (typeof val !== 'string') {
      return JSON.stringify(val)
    }

    const [opt, extra] = val.split('(')

    return (
      <>
        {opt}

        {extra && <span style={{ color: '#b0b0b0' }}>({extra}</span>}
      </>
    )
  }, [])

  const handleSave = React.useCallback(
    archiving => {
      const vals = { ...values }
      if (archiving) {
        vals.archived = new Date().toISOString()
      }

      const { data, error } = formatPayload(vals)

      if (error) {
        return showError(error)
      }

      if (!changed && !archiving) {
        handleClose()
        return
      }

      const updateSave = () =>
        archiving ? setArchiving(true) : setSaving(true)
      const clearSave = () =>
        archiving ? setArchiving(false) : setSaving(false)
      const cb = (success: boolean) => (success ? handleClose() : clearSave())

      if (!editing) {
        dispatch(createInbox(data, cb))
        dispatch(AddSuccessAlert('Inbox created'))
        return
      }

      setAction(() => {
        updateSave()
        dispatch(updateInbox(values.id, data, cb))
        dispatch(AddSuccessAlert('Changes saved'))
      })
      setTitle('ARE YOU SURE?')
      setV2(true)

      if (!archiving) {
        setReason('Updating settings will take place immediately.')
        setSubtext('Everything using this inbox will be affected.')
        return
      }

      setReason(
        <span style={{ fontStyle: 'italic' }}>
          This will archive the inbox. It will no longer be available. Sessions
          associated with this inbox will{' '}
          <span style={{ fontWeight: theme.bold.bold }}>not</span> be
          re-assigned.
        </span>
      )
      setSubtext(
        <span style={{ fontStyle: 'italic', fontWeight: theme.bold.bold }}>
          You cannot archive an inbox that is currently being used by an
          integration.
        </span>
      )
    },
    [
      values,
      showError,
      changed,
      editing,
      handleClose,
      setTitle,
      setAction,
      setReason,
      setSubtext,
      setV2,
      dispatch,
      theme
    ]
  )

  if (![values, timezonesLoaded, teamsLoaded].every(Boolean)) {
    return null
  }

  return (
    <>
      {/* @ts-ignore */}
      <BaseModal
        open
        showTitleOnMobile={true}
        onClose={() => {
          const action = handleClose

          if (changed) {
            setReason('You have unsaved changes')
            setAction(action)
            setV2(true)
          } else {
            action()
          }
        }}
        title={`${editing ? 'Edit' : 'Create'} Inbox`}
        PaperProps={{ style: { width: '50%' } }}
      >
        <ModalTextField title='Name' className={classes.input}>
          <input
            placeholder='Inbox name'
            defaultValue={data?.name}
            onBlur={e => {
              const val = e.target.value
              setValues(old => ({ ...old, name: val }))
              if (val !== data?.name) {
                setChanged(true)
              }
            }}
          />
        </ModalTextField>

        <div className={classes.dropdown}>
          <div>Online Profile</div>

          <Dropdown
            options={Object.keys(PROFILE_OPTIONS)}
            renderOption={renderProfile}
            selected={values.online_profile}
            onSelect={handleChange('online_profile')}
            hideAdd
            hideClose
          >
            <div className='menu'>
              <div>{renderProfile(values.online_profile)}</div>

              <ArrowDown2 />
            </div>
          </Dropdown>
        </div>

        <Checkbox
          checked={values.user_override}
          onCheck={val => {
            handleChange('user_override')(val)
            if (val) {
              handleChange('all_teams')(false)
            }
          }}
          className={classes.topPad}
        >
          Allow user(s) online status to override inbox online status
        </Checkbox>

        <div
          style={{ color: '#b0b0b0', fontStyle: 'italic', marginTop: '0.5rem' }}
        >
          This will effectively override the online profile and hours with the
          online status of any users that can access this inbox. This is
          effective for smaller inboxes, but is not recommended for inboxes with
          lots of users
        </div>

        <Section name='Online Hours'>
          {Object.entries(values.hours).map(([id, hours]) => (
            <React.Fragment key={id}>
              <div className={classes.hoursBlock}>
                <div className={classes.hours}>
                  <div>Start:</div>

                  <TimePicker
                    value={moment(hours.start)}
                    onChange={handleUpdateHours('start', id)}
                    ampm={false}
                    desktopModeMediaQuery={ALWAYS_FALSE_QUERY}
                    className='input'
                  />

                  <div>End:</div>

                  <TimePicker
                    value={moment(hours.end)}
                    onChange={handleUpdateHours('end', id)}
                    ampm={false}
                    desktopModeMediaQuery={ALWAYS_FALSE_QUERY}
                    className='input'
                  />

                  <CloseCircle
                    onClick={handleDeleteHours(id)}
                    className='delete'
                  />
                </div>

                <div className={classes.hoursTicks}>
                  {DAYS.map(day => {
                    const selected = hours.days.includes(day)

                    return (
                      <div key={day} className={classes.hoursTick}>
                        <Checkbox
                          checked={selected}
                          onCheck={() => handleUpdateHours('day', id)(day)}
                        />

                        <div>{day.toLocaleUpperCase()}</div>
                      </div>
                    )
                  })}
                </div>
              </div>

              {isBefore(new Date(hours.end), new Date(hours.start)) && (
                <div className={classes.warning}>
                  End time cannot be before start time
                </div>
              )}

              <Divider className={classes.divider} />
            </React.Fragment>
          ))}

          <Tooltip
            title={
              addHoursDisabled
                ? 'Only up to three online hours can be added'
                : null
            }
            placement='right'
          >
            <div
              onClick={() =>
                addHoursDisabled
                  ? null
                  : handleAddHours({
                      newStart: MIN_TIME,
                      newEnd: MAX_TIME,
                      newDays: DAYS
                    })
              }
              className={classes.addHours}
            >
              <AddCircle className='button' />
              Add new time block
            </div>
          </Tooltip>

          <Divider className={classes.divider} />

          <div className={classes.dropdown}>
            <div>Hours timezone</div>

            <Dropdown
              options={timezones.map(tz => tz.key)}
              renderOption={getTimeZoneDropdownRenderValue}
              selected={values.timezone ?? timezones[0].key}
              onSelect={handleChange('timezone')}
              hideAdd
              hideClose
              stringOnly
              disablePortal
              loading={!timezonesLoaded}
            >
              <div className='menu'>
                <div>{getTimeZoneDropdownRenderChildren()}</div>

                <ArrowDown2 />
              </div>
            </Dropdown>
          </div>
        </Section>

        <Section name='Inbox Access'>
          {!values.user_override && (
            <Checkbox
              checked={values.all_teams}
              onCheck={handleChange('all_teams')}
            >
              Allow all teams to access this inbox{' '}
              <span style={{ color: '#b0b0b0' }}>(open to all)</span>
            </Checkbox>
          )}

          {!values.all_teams && (
            <div className={classes.roleSection}>
              <div className={classes.roleTitle}>Restrict to teams</div>

              <div className={classes.roleList}>
                <AutoInput
                  addText='Add Teams'
                  options={teamOptions}
                  renderOption={(props, option) => (
                    <span
                      {...props}
                      style={{
                        fontSize: theme.size.base,
                        fontFamily: '"Inter", sans-serif'
                      }}
                    >
                      {option.name}
                    </span>
                  )}
                  onSelect={handleTeamSelect}
                />

                {values.team_restrict.map((team, i) => (
                  <div key={`${team}-${i}`} className={classes.box}>
                    {teams.find(t => t.id === team)?.name ?? 'Unknown Team'}

                    <Clear
                      onClick={() => handleTeamSelect({ id: team })}
                      fill='#8F8F8F'
                      className={classes.removeRoleIcon}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </Section>

        <div className={classes.endControls}>
          {editing && (
            <RedButton
              onClick={() => handleSave(true)}
              saving={archiving}
              disabled={saving || archiving}
              className={classes.button}
            >
              Archive
            </RedButton>
          )}

          <Button
            onClick={() => handleSave(false)}
            loading={saving}
            disabled={saving || archiving}
            className={classes.button}
          >
            Save
          </Button>
        </div>
      </BaseModal>
    </>
  )
}

export default BaseInboxModal
